import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	dialogContentClasses,
	Divider,
	Grid,
	Icon,
	IconButton,
	LinearProgress,
	Menu,
	MenuItem,
	paperClasses,
	Skeleton,
	TextField as MuiTextField,
	Tooltip,
	Typography
} from '@mui/material';
import { styled } from '@mui/system';

import DateTime from 'Lib/components/common/DateTime';
import { MuiTable } from 'Lib/components/common/table';
import FileName from 'Lib/components/common/table/filesTable/components/FileName';
import { getApiByPortalType } from 'Lib/components/common/table/filesTable/utils/api';
import MoreOptions from 'Lib/components/submenus/MoreOptions';
import PortalTypes from 'Lib/constants/app/portalTypes';
import Components from 'Lib/constants/security/components';
import FilePermissions from 'Lib/constants/security/components/files';
import useConfig from 'Lib/hooks/api/useConfig';
import ConfirmDialog from 'Lib/muiComponents/dialog/ConfirmDialog';
import DeleteDialog from 'Lib/muiComponents/dialog/DeleteDialog';
import FormDialog from 'Lib/muiComponents/dialog/FormDialog';
import ProcessingDialog from 'Lib/muiComponents/dialog/ProcessingDialog';
import TextField from 'Lib/muiComponents/form/TextField';
import FileCarouselModal from 'Lib/muiComponents/modal/FileCarouselModal';
import Modal from 'Lib/muiComponents/modal/Modal';
import ModalBody from 'Lib/muiComponents/modal/ModalBody';
import ModalClose from 'Lib/muiComponents/modal/ModalClose';
import ModalHeader from 'Lib/muiComponents/modal/ModalHeader';
import ModalTitle from 'Lib/muiComponents/modal/ModalTitle';
import { isPath } from 'Lib/utilities/files';
import { screenSize } from 'Lib/utilities/screenSize';

import { VIEWS } from '../controls/TableView';

import { useFiles } from './context';

/**
 * `FilesTable` displays a table of files and folders associated with a specific object.
 * It supports operations like search, file preview, file and folder actions (e.g., rename, move, delete),
 * and integrates with a custom context for state management.
 *
 * @component
 * @returns {JSX.Element} The `FilesTable` component.
 */

const FilesTable = () => {
	const {
		objectID,
		objectTypeID,
		contactTypeID,
		portalTypeID,
		disableUpload,
		files,
		folders,
		selectedFiles,
		folderContents,
		breadcrumbs,
		isLoading,
		isDownloading,
		emptyMessage,
		shareContacts,
		deleteModal,
		editModal,
		shareFileModal,
		handlePathUpdate,
		handleFileUpdate,
		fetchPaths,
		handleSearch,
		onShareFile,
		handleDownload,
		toggleEditModal,
		toggleMoveFileModal,
		toggleDeleteModal,
		sharedWith,
		handleBatchDownload,
		toggleSharedWithMultiMenu,
		sharedWithMenu,
		sharedMultiMenuActions,
		toggleShareWithMultiModal,
		resetShareModal,
		handleShareMultiSubmit,
		toggleShareWith,
		setShareFileModal,
		isUploading,
		setMoveFile,
		moveFile,
		setAddingNewPath,
		addingNewPath,
		handleDelete,
		handleUpdate,
		confirmMove,
		handleNewPathInput,
		createNewFolder,
		resetAddingNewPath,
		onDrop,
		onRowDrop,
		setClearSelected,
		onRowSelected,
		onRowClick,
		onRowDragOver,
		onDragStart,
		onDragLeave,
		onDragEnd,
		onDropFiles,
		confirmShareWithModal,
		toggleConfirmShareWithModal,
		handleConfirmMove
	} = useFiles();

	const api = getApiByPortalType(portalTypeID);
	const {
		portal: { isTenant, isManager, isOwner }
	} = useConfig();
	const contactID = useSelector(
		state =>
			state.app.portal?.[PortalTypes.getName(portalTypeID)?.toLowerCase()]
				?.contactID
	);
	const leaseID = useSelector(state => state.app.lease?.lease?.leaseID);
	const portfolioID = useSelector(state => state.app.portfolio?.portfolioID);

	const { mobile: isMobileScreenSize, tablet: isTabletScreenSize } =
		screenSize();
	const mobile = isMobileScreenSize || isTabletScreenSize;
	const placeholder = 'Search file by name';

	useEffect(() => {
		if (!objectID || !objectTypeID || !isManager || files.length === 0)
			return;
		fetchPaths();
	}, [files]);

	const moreActions = (data, filesCard = false) => {
		const shareStatus = {
			Tenant: isPath(data)
				? data.isSharedWithTenant == '1'
				: data.fileAttachment.isSharedWithTenant == '1',
			Owner: isPath(data)
				? data.isSharedWithOwner == '1'
				: data.fileAttachment.isSharedWithOwner == '1',
			Vendor: isPath(data)
				? data.isSharedWithVendor == '1'
				: data.fileAttachment.isSharedWithVendor == '1'
		};

		const shareText = type =>
			shareStatus[type]
				? `Stop sharing with ${type}`
				: `Share with ${type}`;

		const shareOption = (type, permission, allowShare) =>
			isManager &&
			api.hasPermission(Components.Files, permission) &&
			allowShare(objectTypeID, contactTypeID)
				? {
						label: shareText(type),
						onClick: () =>
							onShareFile({
								type,
								fileObject: isPath(data)
									? data
									: data.fileAttachment,
								api: isPath(data)
									? handlePathUpdate
									: handleFileUpdate,
								sharedWith: shareStatus[type],
								isSharing: isPath(data)
									? data[`isSharedWith${type}`] == '0'
									: data.fileAttachment[
											`isSharedWith${type}`
										] == '0'
							})
					}
				: null;

		const downloadOption = [
			{ label: 'Download', onClick: () => handleDownload(data) }
		];

		const basicOptions = [
			isManager && {
				label: 'Rename',
				onClick: () => toggleEditModal(data)
			},
			isManager && {
				label: 'Move',
				onClick: () => toggleMoveFileModal(data)
			},
			isManager && {
				label: 'Delete',
				onClick: () => toggleDeleteModal('single', data)
			}
		].filter(Boolean);

		const permissionOptions = isManager
			? [
					shareOption(
						'Owner',
						FilePermissions.ShareFilesWithOwner,
						api.allowOwnerShare
					),
					shareOption(
						'Tenant',
						FilePermissions.ShareFilesWithTenant,
						api.allowTenantShare
					),
					shareOption(
						'Vendor',
						FilePermissions.ShareFilesWithVendor,
						api.allowVendorShare
					)
				].filter(Boolean)
			: [];

		const options = [
			...downloadOption,
			...basicOptions,
			...permissionOptions
		];

		return (
			<MoreOptions
				variant={filesCard ? 'filesCard' : ''}
				options={() => options}
			/>
		);
	};

	const getImageUrl = (data, type = 'preview') => {
		const basePath = `/api/${isTenant ? 'resident/lease' : PortalTypes.getName(portalTypeID)?.toLowerCase()}/files/${data.file.fileID}/${type}`;
		const leaseParam = isTenant ? `leaseID=${leaseID}&` : '';
		const portfolioParam = isOwner ? `portfolioID=${portfolioID}&` : '';
		const params = `${leaseParam}${portfolioParam}objectTypeID=${objectTypeID}&objectID=${objectID}&portalID=${contactID}`;

		return isManager ? basePath : `${basePath}?${params}`;
	};

	const columns = [
		{ label: 'Name', sortBy: 'fileName' },
		isManager && { label: 'Shared' },
		{ label: 'Date Created', sortBy: 'dateCreated' },
		{ label: 'From' },
		{ label: '' }
	].filter(Boolean);

	const rows = [
		{
			render: data => <FileName data={data} getImageUrl={getImageUrl} />,
			sx: () => ({ minWidth: '350px' })
		},
		isManager && {
			render: data =>
				sharedWith(isPath(data) ? data : data.fileAttachment),
			sx: () => ({ minWidth: '150px' })
		},
		{
			render: data => (
				<DateTime
					value={isPath(data) ? null : data.file.dateTimeCreated}
				/>
			)
		},
		{
			render: data =>
				isPath(data) ? (
					'--'
				) : (
					<Chip
						label={PortalTypes.getName(
							data.fileAttachment.portalTypeID
						)}
					/>
				)
		},
		{
			render: data => moreActions(data),
			sx: () => ({ minWidth: '100px' })
		}
	].filter(Boolean);

	const renderDetails = data => ({
		thumbnail: data.file?.previewFileName && getImageUrl(data, 'thumbnail'),
		name: isPath(data) ? data.path : data.file.title,
		shared: sharedWith(isPath(data) ? data : data.fileAttachment),
		date: (
			<DateTime value={isPath(data) ? null : data.file.dateTimeCreated} />
		),
		from: isPath(data)
			? '--'
			: PortalTypes.getName(data.fileAttachment.portalTypeID),
		download: () => handleDownload(data),
		actions: data => moreActions(data, true)
	});

	const selectedActions = () => (
		<Box display="flex" justifyContent="space-between" width="100%">
			<Box display="flex" sx={{ gap: mobile ? 0 : 4 }}>
				{mobile ? (
					<Tooltip title="Download Selected Items">
						<MobileActionButton onClick={handleBatchDownload}>
							<Icon
								sx={{
									fontSize: '24px !important',
									color: 'common.darkblue'
								}}>
								download
							</Icon>
						</MobileActionButton>
					</Tooltip>
				) : (
					<DesktopAsyncActionButton
						mobile={mobile}
						type="submit"
						variant="contained"
						loadingPosition="start"
						loading={isDownloading}
						disabled={isDownloading}
						startIcon={
							<Icon sx={{ fontSize: '24px !important' }}>
								download
							</Icon>
						}
						onClick={handleBatchDownload}>
						Download
					</DesktopAsyncActionButton>
				)}
				{isManager &&
					(mobile ? (
						<Tooltip title="Move Selected Items">
							<MobileActionButton
								onClick={() =>
									toggleMoveFileModal(selectedFiles, true)
								}>
								<Icon
									sx={{
										fontSize: '24px !important',
										color: 'common.darkblue'
									}}>
									drive_file_move
								</Icon>
							</MobileActionButton>
						</Tooltip>
					) : (
						<DesktopActionButton
							variant="primary"
							onClick={() =>
								toggleMoveFileModal(selectedFiles, true)
							}
							mobile={mobile}>
							Move Selected Items
						</DesktopActionButton>
					))}
				{isManager &&
					sharedMultiMenuActions().length > 0 &&
					(mobile ? (
						<Tooltip title="Share Selected Items">
							<MobileActionButton
								id="shared-with-button-mobile"
								aria-haspopup="true"
								onClick={toggleSharedWithMultiMenu}>
								<Icon
									sx={{
										fontSize: '24px !important',
										color: 'common.darkblue'
									}}>
									ios_share
								</Icon>
							</MobileActionButton>
						</Tooltip>
					) : (
						<DesktopActionButton
							id="shared-with-button"
							variant="primary"
							endIcon={
								<Icon sx={{ fontSize: '24px !important' }}>
									expand_more
								</Icon>
							}
							onClick={toggleSharedWithMultiMenu}
							mobile={mobile}>
							Share Selected
						</DesktopActionButton>
					))}
				{isManager && (
					<Menu
						id="shared-with-menu"
						MenuListProps={{
							'aria-labelledby': 'shared-with-menu-button'
						}}
						anchorEl={sharedWithMenu.anchorEl}
						open={sharedWithMenu.status}
						onClose={toggleSharedWithMultiMenu}
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						transformOrigin={{
							vertical: 'bottom',
							horizontal: 'right'
						}}>
						{sharedMultiMenuActions().map(
							({ label, onClick }, index) => (
								<MenuItem key={index} onClick={e => onClick(e)}>
									{label}
								</MenuItem>
							)
						)}
					</Menu>
				)}
			</Box>
			{isManager && (
				<Box>
					<DesktopActionButton
						variant="error"
						color="error"
						onClick={() => toggleDeleteModal('multi')}
						mobile={mobile}>
						{mobile ? 'Delete' : 'Delete Selected'}
					</DesktopActionButton>
				</Box>
			)}
		</Box>
	);

	const renderDeleteModal = () => {
		return (
			<DeleteDialog
				open={deleteModal.status}
				isDeleting={deleteModal.deleting}
				toggle={toggleDeleteModal}
				onDelete={handleDelete}>
				{`Permanently delete ${deleteModal.multi ? 'these' : 'this'} file${deleteModal.multi ? 's' : ''} and places where it's shared?`}
			</DeleteDialog>
		);
	};

	const renderEditModal = () => (
		<FormDialog
			open={editModal.status ?? false}
			title="Rename"
			ButtonProps={{ text: 'Update' }}
			toggle={toggleEditModal}
			onSubmit={handleUpdate}
			initialValues={
				isPath(editModal.data)
					? { path: editModal.data?.path }
					: { title: editModal.data?.file?.title }
			}
			sx={{ [`& .${paperClasses.root}`]: { width: { sm: '400px' } } }}>
			{() => (
				<TextField
					id={isPath(editModal.data) ? 'path' : 'title'}
					name={isPath(editModal.data) ? 'path' : 'title'}
					label="Name"
					placeholder={`Enter ${isPath(editModal.data) ? 'Folder' : 'File'} Name`}
					required
				/>
			)}
		</FormDialog>
	);

	const renderShareModal = () => {
		const title = `${shareFileModal.data?.isSharing ? 'Share' : 'Unshare'} ${shareFileModal.multi && selectedFiles.length > 1 ? 'Files' : 'File'}`;
		const prompt = () => {
			if (shareFileModal.multi)
				return `${shareFileModal.data?.isSharing ? 'Share' : 'Unshare'} ${selectedFiles.length === 1 ? 'this file' : 'these files'} with ${PortalTypes.getName(
					shareFileModal.data?.type
				)?.toLowerCase()}?`;

			return `${shareFileModal.data?.isSharing ? 'Share' : 'Unshare'} this file with ${shareFileModal.data?.type?.toLowerCase()}?`;
		};

		return (
			<FormDialog
				open={shareFileModal.status}
				title={title}
				ButtonProps={{
					text: `Yes, ${shareFileModal.data?.isSharing ? 'Share' : 'Unshare'}`
				}}
				toggle={
					shareFileModal.multi
						? toggleShareWithMultiModal
						: resetShareModal
				}
				onSubmit={
					shareFileModal.multi
						? handleShareMultiSubmit
						: toggleShareWith
				}
				initialValues={shareFileModal}
				sx={{
					[`& .${paperClasses.root}`]: { width: { sm: '400px' } }
				}}>
				{form => {
					form.dirty = true;

					return (
						<>
							<Typography>{prompt()}</Typography>
							<Box mt={6}>
								<Typography
									sx={theme => ({
										mb: 2,
										fontSize: theme.typography.pxToRem(14),
										fontWeight:
											theme.typography.fontWeightSemiBold
									})}>
									Email notification to:
								</Typography>
								<Box
									display="flex"
									borderRadius={1}
									sx={theme => ({
										border: shareFileModal.sendEmail
											? `2px solid ${theme.palette.success.main}`
											: '1px solid #DDD',
										padding: shareFileModal.sendEmail
											? '11px 11px 9px 14px'
											: '12px 12px 10px 15px'
									})}>
									{shareContacts.status ? (
										<Box
											width="100%"
											display="flex"
											flexDirection="column"
											gap={2}>
											{Array.from({ length: 4 }).map(
												(_, index) => (
													<Box key={index}>
														<Skeleton
															variant="text"
															sx={{
																fontSize:
																	'16px',
																width: '6rem'
															}}
														/>
														<Skeleton
															variant="text"
															sx={{
																fontSize:
																	'16px',
																width: '14rem'
															}}
														/>
													</Box>
												)
											)}
										</Box>
									) : (
										<Box
											width="100%"
											display="flex"
											flexDirection="column"
											gap={2}>
											{shareContacts.data.length > 0 ? (
												shareContacts.data.map(
													(contact, index) => (
														<Box key={index}>
															<Typography>
																{contact.name}
															</Typography>
															<Typography
																sx={theme => ({
																	fontSize:
																		theme.typography.pxToRem(
																			12
																		),
																	fontWeight:
																		theme
																			.typography
																			.fontWeightSemiBold
																})}>
																{contact.email ||
																	'No email found'}
															</Typography>
														</Box>
													)
												)
											) : (
												<Box
													minHeight={240}
													display="flex"
													justifyContent="center"
													alignItems="center">
													<Typography>
														No email records
													</Typography>
												</Box>
											)}
										</Box>
									)}
									{(shareContacts.status ||
										shareContacts.data.length > 0) && (
										<Box>
											<Checkbox
												disableRipple
												disabled={
													shareContacts.status ||
													!shareContacts.data.some(
														contact => contact.email
													)
												}
												checked={
													shareFileModal.sendEmail
												}
												onChange={e => {
													form.setFieldValue(
														'sendEmail',
														e.target.checked
													);
													setShareFileModal({
														...shareFileModal,
														sendEmail:
															e.target.checked
													});
												}}
												sx={{ p: 0 }}
											/>
										</Box>
									)}
								</Box>
							</Box>
						</>
					);
				}}
			</FormDialog>
		);
	};

	const renderUploadingModal = () => (
		<ProcessingDialog
			title="Upload"
			statusText="Files uploading.."
			open={isUploading}></ProcessingDialog>
	);

	const renderShareWithAlertModal = () => (
		<ConfirmDialog
			title={`Share ${moveFile.data?.length > 1 ? 'these files' : 'this file'}?`}
			buttonText="Move"
			open={confirmShareWithModal.status}
			toggle={toggleConfirmShareWithModal}
			onConfirm={handleConfirmMove}>
			{`${moveFile.data?.length > 1 ? 'These files' : 'This file'} will be visible to everyone who can see ${moveFile.newDirectoryName}`}
		</ConfirmDialog>
	);

	const renderMoveModal = () => {
		if (!moveFile.data) return null;

		const setNewDirectory = (form, newDirectory) => {
			const newDirectoryName = `${files.paths?.find(path => path.pathID === newDirectory)?.path || 'the selected'} folder`;

			setMoveFile({ ...moveFile, newDirectory, newDirectoryName });
			form.setFieldValue('newDirectory', newDirectory);
		};

		return (
			<FormDialog
				open={moveFile.status}
				title="Move"
				ButtonProps={{ text: 'Move' }}
				toggle={() => toggleMoveFileModal()}
				onSubmit={confirmMove}
				sx={{
					[`& .${paperClasses.root}`]: {
						width: { sm: '569px' },
						display: 'flex',
						flexDirection: 'column',
						maxHeight: { sm: '90vh' }
					},
					[`& .${dialogContentClasses.root}`]: {
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'row',
						flex: 1
					}
				}}>
				{form => {
					return (
						<Grid container spacing={8}>
							<Grid
								item
								xs={5}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									maxHeight: '100%'
								}}>
								<Typography
									sx={theme => ({
										fontSize: theme.typography.pxToRem(22)
									})}>
									Selected Items
								</Typography>
								<Typography
									mb={2}
									sx={theme => ({
										fontSize: theme.typography.pxToRem(12),
										mt: 1
									})}>
									Total Items:{' '}
									{moveFile.multi ? moveFile.data.length : 1}
								</Typography>
								<Box sx={{ overflowY: 'auto', flex: 1 }}>
									{moveFile.multi ? (
										<Box
											display="flex"
											flexDirection="column"
											gap={3}>
											{moveFile.data?.map(
												(item, index) => (
													<Box key={index}>
														<FileName
															data={item}
															modal={true}
															getImageUrl={
																getImageUrl
															}
														/>
														{index !==
															moveFile.data
																.length -
																1 && (
															<Divider
																sx={{ mt: 3 }}
															/>
														)}
													</Box>
												)
											)}
										</Box>
									) : (
										moveFile.data && (
											<FileName
												data={moveFile.data}
												modal={true}
												getImageUrl={getImageUrl}
											/>
										)
									)}
								</Box>
							</Grid>
							<Grid
								item
								xs={7}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									maxHeight: '100%'
								}}>
								<Box
									display="flex"
									flexDirection="column"
									maxHeight="100%"
									gap={3}>
									<Typography
										sx={theme => ({
											fontSize:
												theme.typography.pxToRem(22)
										})}>
										Move to
									</Typography>
									<Divider />
									<Box
										display="flex"
										flexDirection="column"
										gap={3}
										sx={{ overflowY: 'auto', flex: 1 }}>
										{folders?.map(folder => (
											<Box
												key={folder.id}
												display="flex"
												flexDirection="column"
												gap={3}>
												<Box
													display="flex"
													justifyContent="space-between"
													gap={2}
													sx={{ cursor: 'pointer' }}
													onClick={() =>
														setNewDirectory(
															form,
															folder.id
														)
													}>
													<Box
														display="flex"
														gap={2}
														width="260px">
														<Icon>folder</Icon>
														<Typography noWrap>
															{folder.name}
														</Typography>
													</Box>
													{form.values
														.newDirectory ===
														folder.id && (
														<Icon>done</Icon>
													)}
												</Box>
												<Divider />
											</Box>
										))}
										{addingNewPath.status ? (
											<Box>
												{addingNewPath.isAdding ? (
													<Box
														mt={2}
														px={4}
														display="flex"
														alignItems="center">
														<LinearProgress />
													</Box>
												) : (
													<Box
														display="flex"
														gap={4}
														alignItems="center">
														<MuiTextField
															autoFocus
															fullWidth
															placeholder="Folder Name"
															value={
																addingNewPath.path
															}
															onChange={
																handleNewPathInput
															}
															onKeyDown={e => {
																if (
																	e.key ===
																		'Enter' &&
																	addingNewPath.path?.trim()
																)
																	createNewFolder();
															}}
														/>
														<Tooltip
															arrow
															title="Cancel"
															placement="top">
															<IconButton
																variant="small"
																onClick={
																	resetAddingNewPath
																}
																sx={{
																	color: 'error.main'
																}}>
																<Icon>
																	cancel
																</Icon>
															</IconButton>
														</Tooltip>
														<Tooltip
															arrow
															title="Create Folder"
															placement="top">
															<Box component="span">
																<IconButton
																	variant="small"
																	onClick={
																		createNewFolder
																	}
																	disabled={
																		!addingNewPath.path?.trim()
																	}
																	sx={{
																		color: 'success.main'
																	}}>
																	<Icon>
																		done
																	</Icon>
																</IconButton>
															</Box>
														</Tooltip>
													</Box>
												)}
											</Box>
										) : (
											<Box
												display="flex"
												gap={2}
												sx={{ cursor: 'pointer' }}
												onClick={() =>
													setAddingNewPath({
														...addingNewPath,
														status: true
													})
												}>
												<Icon
													sx={{
														color: 'success.main'
													}}>
													create_new_folder
												</Icon>
												<Typography
													sx={{
														color: 'success.main'
													}}>
													Add New Folder
												</Typography>
											</Box>
										)}
									</Box>
								</Box>
							</Grid>
						</Grid>
					);
				}}
			</FormDialog>
		);
	};

	return (
		<Box display="flex" flexDirection="column" height="100%">
			<MuiTable
				files
				hideFilters
				disableUpload={disableUpload}
				breadcrumbs={breadcrumbs}
				onBreadcrumbDrop={onDrop}
				checkable={data => (isPath(data) ? 'pathID' : 'file.fileID')}
				rows={rows}
				columns={columns}
				loading={isLoading}
				data={folderContents}
				renderDetails={renderDetails}
				fetch={handleSearch}
				common={{ actions: api.tableActions }}
				emptyMessage={emptyMessage}
				emptyIcon="folder_open"
				setClearSelected={setClearSelected}
				selectedActions={selectedActions}
				onSelected={onRowSelected}
				search={placeholder}
				onRowClick={onRowClick}
				onRowDrop={onRowDrop}
				onRowDragOver={onRowDragOver}
				onRowDragStart={isManager && onDragStart}
				onRowDragLeave={isManager && onDragLeave}
				onRowDragEnd={onDragEnd}
				onDropFiles={onDropFiles}
				views={[VIEWS.LIST, VIEWS.CARD]}
				// TODO: Chaging the cursor until the entire row is clickable for previewing a file at which point we can remove this
				sx={{
					borderRadius: '4px 4px 12px 12px',
					'& .MuiTableRow-root': { cursor: 'default' }
				}}
			/>
			{!isLoading && (
				<Modal name="File Preview">
					<ModalHeader
						sx={{ bgcolor: ['common.black', null, 'transparent'] }}>
						<ModalTitle
							sx={{
								color: ['common.white', null, 'common.black']
							}}
						/>
						<ModalClose
							sx={{
								color: ['common.white', null, 'common.black']
							}}
						/>
					</ModalHeader>
					<ModalBody>
						<FileCarouselModal
							files={files}
							objectID={objectID}
							objectTypeID={objectTypeID}
							portalID={contactID}
						/>
					</ModalBody>
				</Modal>
			)}
			{renderEditModal()}
			{renderDeleteModal()}
			{renderShareModal()}
			{renderMoveModal()}
			{renderUploadingModal()}
			{renderShareWithAlertModal()}
		</Box>
	);
};

const MobileActionButton = styled(Box)(({ theme }) => ({
	borderRadius: '10px',
	height: '40px',
	width: '40px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	cursor: 'pointer',
	'&:hover': {
		background: theme.palette.common.lightblue,
		border: '1px solid #91BDDE'
	}
}));

const DesktopActionButton = styled(Button, {
	shouldForwardProp: prop => prop !== 'mobile'
})(({ theme, mobile }) => ({
	height: 40,
	fontSize: theme.typography.pxToRem(mobile ? 14 : 16),
	borderRadius: mobile ? '12px' : '40px'
}));

const DesktopAsyncActionButton = styled(LoadingButton, {
	shouldForwardProp: prop => prop !== 'mobile'
})(({ theme, mobile }) => ({
	height: 40,
	fontSize: theme.typography.pxToRem(mobile ? 14 : 16),
	borderRadius: mobile ? '12px' : '40px',
	backgroundColor: theme.palette.success.main,
	color: theme.palette.common.white,
	boxShadow: 'none',
	'&:hover': {
		backgroundColor: theme.palette.success.main,
		boxShadow:
			'0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2)'
	},
	'&:focus': {
		background: '#029745'
	}
}));

export default FilesTable;
